import React, { useEffect, useRef, useCallback, useMemo } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js'
import { resolveCheckoutPublishableKey, confirmCheckout } from '../../lib/billingApi'

interface CheckoutModalProps {
  clientSecret: string | null
  sessionId: string | null
  publishableKey?: string | null
  onClose: () => void
  onComplete?: () => void
}

export default function CheckoutModal({ clientSecret, sessionId, publishableKey, onClose, onComplete }: CheckoutModalProps) {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const completedRef = useRef(false)
  const sessionIdRef = useRef(sessionId)
  const { publishableKey: effectivePublishableKey, source: publishableKeySource } = useMemo(
    () => resolveCheckoutPublishableKey({ runtimePublishableKey: publishableKey }),
    [publishableKey]
  )
  const stripePromise = useMemo(
    () => (effectivePublishableKey ? loadStripe(effectivePublishableKey) : null),
    [effectivePublishableKey]
  )
  sessionIdRef.current = sessionId

  const handleDone = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    onComplete?.()
  }, [onComplete])

  useEffect(() => {
    if (!clientSecret || !sessionId) return
    completedRef.current = false
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Safety-net polling: start after 1s, poll every 1.5s.
    // Primary close path is via Stripe's onComplete callback.
    const startDelay = setTimeout(() => {
      pollRef.current = setInterval(async () => {
        if (completedRef.current || !sessionIdRef.current) return
        try {
          const result = await confirmCheckout(sessionIdRef.current)
          if (result.status === 'success') {
            handleDone()
          }
        } catch {
          // session not complete yet, keep polling
        }
      }, 1500)
    }, 1000)

    return () => {
      document.body.style.overflow = prev
      clearTimeout(startDelay)
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
  }, [clientSecret, sessionId, handleDone])

  if (!clientSecret) return null
  if (!effectivePublishableKey) {
    const message = publishableKeySource === 'missing-runtime'
      ? 'The server did not return a Stripe publishable key for this deployment. Redeploy the backend and frontend with matching Stripe test/live keys and try again.'
      : 'Stripe is not configured for this environment. Please update the publishable key and try again.'
    return (
      <div className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center bg-black/50">
        <div className="bg-white dark:bg-gray-900 rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-red-200 dark:border-red-900/40">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payment unavailable</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {message}
          </p>
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-lg max-h-[92dvh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Complete Payment</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-1">
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret, onComplete: handleDone }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  )
}
